import { baseApi } from './baseApi';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type MoneyCurrency = 'UYU' | 'USD';

export type MoneyTransactionType =
  | 'commission_credit'
  | 'peer_transfer_in'
  | 'peer_transfer_out'
  | 'fx_conversion_debit'
  | 'fx_conversion_credit'
  | 'credit_purchase_debit'
  | 'withdrawal_debit'
  | 'withdrawal_reversal'
  | 'mp_topup'
  | 'manual_payout'
  | 'admin_adjustment_credit'
  | 'admin_adjustment_debit';

export interface WithdrawalCostLine {
  /** Costo de la transferencia, en la misma moneda del retiro. */
  costo: number;
  /** Lo que efectivamente llega a la cuenta. */
  neto: number;
  /** Por qué se cobra eso — o por qué no se cobra. */
  motivo: string;
  mismoBanco: boolean;
}

export interface WithdrawalCostPreview {
  uyu: WithdrawalCostLine | null;
  usd: WithdrawalCostLine | null;
  /** La cuenta destino es del mismo banco que la empresa: sin costo. */
  mismoBanco: boolean;
}

export type WithdrawAccountType = 'bank' | 'mercadolibre' | 'prex';
export type WithdrawAccountCurrency = 'UYU' | 'USD' | 'both';
export type WithdrawalStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'processing'
  | 'completed'
  | 'paid';

export interface WalletBalance {
  walletId: string;
  balanceUYU: number;
  balanceUSD: number;
  totalCreditedUYU: number;
  totalCreditedUSD: number;
  totalDebitedUYU:  number;
  totalDebitedUSD:  number;
  lastActivityAt?: string;
  recentTransactions: MoneyTransaction[];
}

export interface MoneyTransaction {
  _id: string;
  walletId: string;
  userId: string;
  type: MoneyTransactionType;
  currency: MoneyCurrency;
  amount: number;
  balanceAfterUYU: number;
  balanceAfterUSD: number;
  referenceType?: string;
  referenceId?: string;
  linkedTransactionId?: string;
  peerUserId?: string;
  fxRate?: number;
  fxRateSource?: string;
  description: string;
  meta?: Record<string, unknown>;
  createdAt: string;
}

export interface FxRate {
  buyUSD: number;
  sellUSD: number;
  fetchedAt: string;
  isStale: boolean;
  source: string;
}

export type WithdrawAccountKind = 'SAVINGS' | 'CHECKING';
export type WithdrawDocumentType = 'CI' | 'RUT';

export interface WithdrawAccount {
  _id: string;
  userId: string;
  type: WithdrawAccountType;
  alias: string;
  isDefault: boolean;
  currency: WithdrawAccountCurrency;
  // bank — campos dLocal Go
  bankName?: string;
  bankCode?: string;
  bankBranch?: string;
  accountNumber?: string;
  accountType?: WithdrawAccountKind;
  accountHolder?: string;       // legacy (nombre completo)
  holderFirstName?: string;
  holderLastName?: string;
  documentType?: WithdrawDocumentType;
  documentId?: string;
  holderEmail?: string;
  // mercadolibre
  mlAlias?: string;
  mlCvu?: string;
  // prex
  prexPhone?: string;
  isActive: boolean;
  createdAt: string;
}

export interface FxConversionDetails {
  fromCurrency: MoneyCurrency;
  toCurrency: MoneyCurrency;
  rate: number;
  rateSource: string;
  originalAmount: number;
  convertedAmount: number;
}

export interface WithdrawalRequest {
  _id: string;
  requestedBy: string | { _id: string; name: string; lastName?: string; email?: string; role?: string; office?: string; phone?: string; avatar?: string };
  walletId: string;
  amountUYU: number;
  amountUSD: number;
  accountId: string;
  accountSnapshot: Record<string, unknown>;
  needsFxConversion: boolean;
  fxDetails?: FxConversionDetails;
  status: WithdrawalStatus;
  holdTransactionId?: string;
  adminNote?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  completedAt?: string;
  /** Cuando dLocal rechaza, queda guardado el error pero la solicitud sigue "approved". */
  processingError?: string;
  /** Método con el que se pagó efectivamente: dlocal, cash, manual_transfer. */
  paymentMethod?: 'dlocal' | 'cash' | 'manual_transfer';
  /** Fee aplicado al monto bruto. El banco recibe netUYU/netUSD, no amountUYU/amountUSD. */
  feeUYU?: number;
  feeUSD?: number;
  netUYU?: number;
  netUSD?: number;
  createdAt: string;
}

export interface AdminWalletEntry {
  userId: string;
  name: string;
  email: string;
  role: string;
  officeId?: string;
  avatar?: string | null;
  phone?: string | null;
  balanceUYU: number;
  balanceUSD: number;
  totalCreditedUYU: number;
  totalCreditedUSD: number;
  lastActivityAt?: string;
  walletId: string | null;
}

export interface TransferRecipient {
  _id: string;
  name: string;
  lastName: string;
  email: string;
  role: string;
  officeId: string | null;
  officeName: string | null;
  avatar: string | null;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export type WalletMovementKind =
  | 'commission' | 'transfer_in' | 'transfer_out' | 'fx_conversion'
  | 'withdrawal' | 'withdrawal_reversal' | 'topup' | 'purchase' | 'refund' | 'other';

export type WalletMovementMethodType =
  | 'wallet_uyu' | 'wallet_usd'
  | 'card' | 'credits'
  | 'commission' | 'transfer' | 'fx' | 'withdrawal' | 'topup'
  | 'other';

export interface WalletMovement {
  _id:           string;
  source:        'money_tx' | 'marketplace_order';
  kind:          WalletMovementKind;
  title:         string;
  description?:  string;
  /** Total cobrado al usuario, incluye fee de procesamiento si aplica. */
  amount:        number;
  /** Monto del producto/servicio sin comisión (solo presente en pagos con dLocal). */
  baseAmount?:   number;
  /** Comisión de procesamiento dLocal (solo presente en pagos con dLocal). */
  feeAmount?:    number;
  currency:      'USD' | 'UYU' | 'CREDITS';
  direction:     'in' | 'out';
  status:        'completed' | 'pending' | 'failed' | 'refunded';
  paymentMethod: {
    type:        WalletMovementMethodType;
    label:       string;
    cardBrand?:  string;
    cardLast4?:  string;
  };
  relatedOrderId?: string;
  relatedTxId?:    string;
  createdAt:     string;
  /**
   * Metadata extra del movimiento. Para `commission_credit` viene poblado con
   * el desglose completo de cómo se calculó la comisión.
   */
  commissionMeta?: {
    commissionId?:        string;
    propertyId?:          string;
    propertyAddress?:     string;
    operation?:           'sale' | 'rent';
    transactionValue?:    number;
    commissionTotal?:     number;
    commissionPercentage?: number;
    rangoPctSnapshot?:    number;
    splitPercentage?:     number;
    grossAmount?:         number;
    feeAmount?:           number;
    feeRate?:             number;
    netAmount?:           number;
    currency?:            'USD' | 'UYU';
    isTwoSide?:           boolean;
    isColisting?:         boolean;
    hasReferral?:         boolean;
    hasConnector?:        boolean;
    coAgentName?:         string;
  };
  /** De qué propiedad/operación/agente es la comisión, resuelto desde la Commission
   *  (para mostrar y linkear a la ficha). */
  reference?: {
    operation: string | null;
    property:  { id: string; code: string; title: string } | null;
    agent:     string | null;
  };
}

// ─── Wallet API ───────────────────────────────────────────────────────────────

export type OperationFeeOperation =
  | 'default'
  | 'commission_credit'
  | 'mp_topup'
  | 'peer_transfer'
  | 'withdrawal'
  | 'fx_conversion'
  | 'manual_debit';

export type OperationFeeRates = Record<OperationFeeOperation, number>;

export const walletApi = baseApi.injectEndpoints({
  endpoints: (build) => ({

    // ── Agente: wallet ────────────────────────────────────────────────────────
    getWalletMe: build.query<WalletBalance, void>({
      query: () => '/wallet/me',
      transformResponse: (r: { data: WalletBalance }) => r.data,
      providesTags: ['Wallet'],
    }),

    listTransactions: build.query<PaginatedResponse<MoneyTransaction>, {
      page?: number; limit?: number; type?: string; currency?: MoneyCurrency; from?: string; to?: string;
    }>({
      query: (params) => ({ url: '/wallet/transactions', params }),
      transformResponse: (r: { data: PaginatedResponse<MoneyTransaction> }) => r.data,
      providesTags: ['WalletTransactions'],
    }),

    listWalletMovements: build.query<PaginatedResponse<WalletMovement>, {
      page?: number; limit?: number; kind?: string; direction?: 'in' | 'out';
    }>({
      query: (params) => ({ url: '/wallet/movements', params }),
      transformResponse: (r: { data: PaginatedResponse<WalletMovement> }) => r.data,
      providesTags: ['WalletTransactions', 'MarketplaceOrder', 'Payment'],
    }),

    getFxRate: build.query<FxRate, void>({
      query: () => '/wallet/fx-rate',
      transformResponse: (r: { data: FxRate }) => r.data,
    }),

    convertCurrency: build.mutation<{
      fromCurrency: MoneyCurrency; toCurrency: MoneyCurrency;
      fromAmount: number; toAmount: number; rate: number; rateSource: string; isStale: boolean;
    }, { fromCurrency: MoneyCurrency; amount: number }>({
      query: (body) => ({ url: '/wallet/convert', method: 'POST', body }),
      invalidatesTags: ['Wallet', 'WalletTransactions'],
    }),

    // ── Cuentas de retiro ─────────────────────────────────────────────────────
    listWithdrawAccounts: build.query<WithdrawAccount[], void>({
      query: () => '/wallet/accounts',
      transformResponse: (r: { data: WithdrawAccount[] }) => r.data,
      providesTags: ['WithdrawAccounts'],
    }),

    createWithdrawAccount: build.mutation<WithdrawAccount, Partial<WithdrawAccount>>({
      query: (body) => ({ url: '/wallet/accounts', method: 'POST', body }),
      invalidatesTags: ['WithdrawAccounts'],
    }),

    updateWithdrawAccount: build.mutation<WithdrawAccount, { id: string } & Partial<WithdrawAccount>>({
      query: ({ id, ...body }) => ({ url: `/wallet/accounts/${id}`, method: 'PUT', body }),
      invalidatesTags: ['WithdrawAccounts'],
    }),

    deleteWithdrawAccount: build.mutation<void, string>({
      query: (id) => ({ url: `/wallet/accounts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['WithdrawAccounts'],
    }),

    setDefaultWithdrawAccount: build.mutation<WithdrawAccount, string>({
      query: (id) => ({ url: `/wallet/accounts/${id}/default`, method: 'PUT' }),
      invalidatesTags: ['WithdrawAccounts'],
    }),

    // ── Transferencias ────────────────────────────────────────────────────────
    peerTransfer: build.mutation<void, { toUserId: string; amountUYU?: number; amountUSD?: number; note?: string }>({
      query: (body) => ({ url: '/wallet/transfers', method: 'POST', body }),
      invalidatesTags: ['Wallet', 'WalletTransactions'],
    }),

    // ── Retiros ───────────────────────────────────────────────────────────────
    /**
     * Costo del retiro antes de pedirlo. Lo calcula el backend: depende del banco de
     * destino (a una cuenta del mismo banco de la empresa no cuesta nada) y de tramos
     * en Unidades Indexadas, así que no es un porcentaje que el front pueda estimar.
     */
    previewWithdrawalCost: build.query<WithdrawalCostPreview, { accountId: string; amountUYU: number; amountUSD: number }>({
      query: (params) => ({ url: '/wallet/withdrawals/cost', params }),
      transformResponse: (res: { data: WithdrawalCostPreview }) => res.data,
    }),

    createWithdrawal: build.mutation<WithdrawalRequest, { amountUYU: number; amountUSD: number; accountId: string }>({
      query: (body) => ({ url: '/wallet/withdrawals', method: 'POST', body }),
      invalidatesTags: ['Wallet', 'WalletTransactions', 'WithdrawalRequests'],
    }),

    listMyWithdrawals: build.query<PaginatedResponse<WithdrawalRequest>, { page?: number; limit?: number }>({
      query: (params) => ({ url: '/wallet/withdrawals', params }),
      transformResponse: (r: { data: PaginatedResponse<WithdrawalRequest> }) => r.data,
      providesTags: ['WithdrawalRequests'],
    }),

    mpTopup: build.mutation<{ message: string; amountUYU: number; status: string }, { amountUYU: number }>({
      query: (body) => ({ url: '/wallet/mp-topup', method: 'POST', body }),
    }),

    // ── Destinatarios de transferencia ───────────────────────────────────────
    listTransferRecipients: build.query<TransferRecipient[], void>({
      query: () => '/wallet/transfer-recipients',
      transformResponse: (r: { data: TransferRecipient[] }) => r.data,
    }),

    // ── Marketplace: pago con wallet ──────────────────────────────────────────
    payOrderWithWallet: build.mutation<unknown, string>({
      query: (orderId) => ({ url: `/marketplace/orders/${orderId}/pay-with-wallet`, method: 'POST', body: {} }),
      invalidatesTags: ['Wallet', 'WalletTransactions', 'Credits', 'MarketplaceOrder', 'MarketplaceSlots'],
    }),

    // ── Marketplace: pago con créditos (1 crédito = 1 UYU) ───────────────────
    payOrderWithCredits: build.mutation<unknown, string>({
      query: (orderId) => ({ url: `/marketplace/orders/${orderId}/pay-with-credits`, method: 'POST', body: {} }),
      invalidatesTags: ['Credits', 'MarketplaceOrder', 'MarketplaceSlots'],
    }),

    // ── Admin ─────────────────────────────────────────────────────────────────
    adminListWallets: build.query<
      PaginatedResponse<AdminWalletEntry> & { totals: { balanceUYU: number; balanceUSD: number } },
      { q?: string; officeId?: string; agentId?: string; sort?: 'uyu_desc' | 'uyu_asc' | 'usd_desc' | 'usd_asc'; page?: number; limit?: number }
    >({
      query: (params) => ({ url: '/admin/wallets', params }),
      transformResponse: (r: { data: PaginatedResponse<AdminWalletEntry> & { totals: { balanceUYU: number; balanceUSD: number } } }) => r.data,
      providesTags: ['AdminWallets'],
    }),

    adminGetAgentTransactions: build.query<PaginatedResponse<MoneyTransaction>, { userId: string; page?: number; limit?: number; type?: string; currency?: string }>({
      query: ({ userId, ...params }) => ({ url: `/admin/wallets/${userId}/transactions`, params }),
      transformResponse: (r: { data: PaginatedResponse<MoneyTransaction> }) => r.data,
    }),

    /**
     * Ajuste manual del saldo de un agente. El motivo es obligatorio: viaja al movimiento
     * que después ve el agente en su wallet y queda en la auditoría. (20/08/2026)
     */
    adminAdjustWallet: build.mutation<
      { balanceUYU: number; balanceUSD: number },
      { userId: string; currency: 'UYU' | 'USD'; amount: number; direction: 'credit' | 'debit' | 'set'; reason: string }
    >({
      query: ({ userId, ...body }) => ({ url: `/admin/wallets/${userId}/adjust`, method: 'POST', body }),
      transformResponse: (r: { data: { balanceUYU: number; balanceUSD: number } }) => r.data,
      invalidatesTags: ['AdminWallets', 'Wallet', 'WalletTransactions'],
    }),

    adminListWithdrawals: build.query<PaginatedResponse<WithdrawalRequest> & { pending: number }, {
      status?: string; statuses?: string; agentId?: string; from?: string; to?: string; page?: number; limit?: number;
    }>({
      query: (params) => ({ url: '/admin/wallet/withdrawals', params }),
      transformResponse: (r: { data: PaginatedResponse<WithdrawalRequest> & { pending: number } }) => r.data,
      providesTags: ['AdminWithdrawals'],
    }),

    adminApproveWithdrawal: build.mutation<WithdrawalRequest, { id: string; adminNote: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/wallet/withdrawals/${id}/approve`, method: 'PUT', body }),
      invalidatesTags: ['AdminWithdrawals', 'AdminWallets'],
    }),

    adminRejectWithdrawal: build.mutation<WithdrawalRequest, { id: string; adminNote: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/wallet/withdrawals/${id}/reject`, method: 'PUT', body }),
      invalidatesTags: ['AdminWithdrawals', 'AdminWallets'],
    }),

    /**
     * Admin dispara payout en dLocal. Marca el request como `processing`;
     * el webhook lo lleva a completed/failed después.
     */
    adminProcessWithdrawalDlocal: build.mutation<WithdrawalRequest, { id: string; adminNote?: string }>({
      query: ({ id, ...body }) => ({ url: `/admin/wallet/withdrawals/${id}/process-dlocal`, method: 'POST', body }),
      invalidatesTags: ['AdminWithdrawals', 'AdminWallets'],
    }),

    /** Marca el retiro como pagado manualmente (sin dLocal). */
    adminMarkWithdrawalPaid: build.mutation<WithdrawalRequest, { id: string; adminNote?: string; method?: 'cash' | 'manual_transfer' }>({
      query: ({ id, ...body }) => ({ url: `/admin/wallet/withdrawals/${id}/mark-paid`, method: 'POST', body }),
      invalidatesTags: ['AdminWithdrawals', 'AdminWallets'],
    }),

    /** Config pública del módulo wallet — fuente única del feePct configurable. */
    getWalletConfig: build.query<{ operationFeePct: number; rates: OperationFeeRates }, void>({
      query: () => ({ url: '/wallet/config' }),
      transformResponse: (r: { data: { operationFeePct: number; rates: OperationFeeRates } }) => r.data,
      keepUnusedDataFor: 300, // cache 5min, no cambia seguido
      providesTags: [{ type: 'OperationFee', id: 'CONFIG' }],
    }),

    /** Admin: leer config completa de rates. */
    getOperationFeesConfig: build.query<{ rates: OperationFeeRates }, void>({
      query: () => ({ url: '/admin/wallet/operation-fees-config' }),
      transformResponse: (r: { data: { rates: OperationFeeRates } }) => r.data,
      providesTags: [{ type: 'OperationFee', id: 'CONFIG' }],
    }),

    /** Admin: actualizar config de rates. */
    updateOperationFeesConfig: build.mutation<{ rates: OperationFeeRates }, { rates: OperationFeeRates }>({
      query: (body) => ({ url: '/admin/wallet/operation-fees-config', method: 'PUT', body }),
      transformResponse: (r: { data: { rates: OperationFeeRates } }) => r.data,
      invalidatesTags: [{ type: 'OperationFee', id: 'CONFIG' }],
    }),

    /** Admin: listado paginado de operation fees (tab "Operaciones" en /admin/revenue). */
    adminListOperationFees: build.query<{
      items: Array<{
        _id: string;
        user: { _id: string; name?: string; lastName?: string; email?: string } | string;
        operation: 'commission_credit' | 'mp_topup' | 'peer_transfer' | 'withdrawal' | 'manual_debit' | 'fx_conversion';
        grossAmount: number;
        feeAmount:   number;
        netAmount:   number;
        feeRate:     number;
        currency:    MoneyCurrency;
        description?: string;
        bcuRate?: number;
        effectiveRate?: number;
        createdAt: string;
      }>;
      total: number;
      page:  number;
      limit: number;
      summary: Array<{ _id: { operation: string; currency: MoneyCurrency }; totalFee: number; totalGross: number; count: number }>;
    }, { from?: string; to?: string; operation?: string; currency?: MoneyCurrency; page?: number; limit?: number }>({
      query: (params) => ({ url: '/admin/wallet/operation-fees', params }),
      transformResponse: (r: { data: {
        items: Array<{
          _id: string;
          user: { _id: string; name?: string; lastName?: string; email?: string } | string;
          operation: 'commission_credit' | 'mp_topup' | 'peer_transfer' | 'withdrawal' | 'manual_debit' | 'fx_conversion';
          grossAmount: number; feeAmount: number; netAmount: number; feeRate: number;
          currency: MoneyCurrency;
          description?: string; bcuRate?: number; effectiveRate?: number;
          createdAt: string;
        }>;
        total: number; page: number; limit: number;
        summary: Array<{ _id: { operation: string; currency: MoneyCurrency }; totalFee: number; totalGross: number; count: number }>;
      } }) => r.data,
      providesTags: ['OperationFee'],
    }),
  }),
});

export const {
  useGetWalletMeQuery,
  useListTransactionsQuery,
  useListWalletMovementsQuery,
  useGetFxRateQuery,
  useConvertCurrencyMutation,
  useListWithdrawAccountsQuery,
  useCreateWithdrawAccountMutation,
  useUpdateWithdrawAccountMutation,
  useDeleteWithdrawAccountMutation,
  useSetDefaultWithdrawAccountMutation,
  useListTransferRecipientsQuery,
  usePeerTransferMutation,
  useCreateWithdrawalMutation,
  usePreviewWithdrawalCostQuery,
  useListMyWithdrawalsQuery,
  useMpTopupMutation,
  usePayOrderWithWalletMutation,
  usePayOrderWithCreditsMutation,
  useAdminListWalletsQuery,
  useAdminGetAgentTransactionsQuery,
  useAdminAdjustWalletMutation,
  useAdminListWithdrawalsQuery,
  useAdminApproveWithdrawalMutation,
  useAdminRejectWithdrawalMutation,
  useAdminProcessWithdrawalDlocalMutation,
  useAdminMarkWithdrawalPaidMutation,
  useAdminListOperationFeesQuery,
  useGetWalletConfigQuery,
  useGetOperationFeesConfigQuery,
  useUpdateOperationFeesConfigMutation,
} = walletApi;
